import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useCart } from '../context/CartContext';
import { formatPrice } from '../lib/utils';
import { Trash2, ShoppingBag, ArrowRight, AlertCircle } from 'lucide-react';

const CartPage: React.FC = () => {
  const { cart, removeItem, updateQuantity, clearCart } = useCart();

  return (
    <div className="bg-gray-50 py-8 min-h-[70vh]">
      <div className="container-custom">
        <h1 className="text-2xl font-bold text-navy-800 mb-8">Your Shopping Cart</h1>
        
        {cart.items.length === 0 ? (
          <motion.div 
            className="bg-white rounded-lg shadow-sm p-8 text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <ShoppingBag className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Your cart is empty</h2>
            <p className="text-gray-600 mb-6">
              Looks like you haven't added any products to your cart yet.
            </p>
            <Link 
              to="/products" 
              className="btn btn-primary px-6 py-2 inline-flex items-center space-x-2"
            >
              <span>Continue Shopping</span>
              <ArrowRight className="h-4 w-4" />
            </Link>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2">
              <motion.div 
                className="bg-white rounded-lg shadow-sm overflow-hidden"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
              >
                <ul className="divide-y divide-gray-200">
                  {cart.items.map((item) => {
                    const product = item.product;
                    const price = product.discountPrice || product.price;
                    
                    return (
                      <motion.li 
                        key={product.id} 
                        className="p-4 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        {/* Product Image */}
                        <div className="flex-shrink-0 w-full sm:w-20 h-20 mb-4 sm:mb-0">
                          <img 
                            src={product.image} 
                            alt={product.name} 
                            className="h-full w-full object-cover rounded"
                          />
                        </div>
                        
                        {/* Product Details */}
                        <div className="flex-grow sm:ml-6">
                          <Link to={`/product/${product.id}`} className="text-lg font-medium text-navy-800 hover:text-amber-500 transition-colors">
                            {product.name}
                          </Link>
                          <div className="text-gray-600 text-sm mt-1 capitalize">
                            {product.category} / {product.subcategory.replace(/-/g, ' ')}
                          </div>
                        </div>
                        
                        {/* Quantity and Price */}
                        <div className="flex items-center justify-between w-full sm:w-auto mt-4 sm:mt-0 sm:ml-6">
                          <div className="flex items-center border border-gray-300 rounded-md overflow-hidden">
                            <button 
                              className="px-2 py-1 bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
                              onClick={() => updateQuantity(product.id, Math.max(1, item.quantity - 1))}
                              disabled={item.quantity <= 1}
                            >
                              -
                            </button>
                            <input 
                              type="number" 
                              min="1"
                              max={product.stock}
                              value={item.quantity}
                              onChange={(e) => updateQuantity(product.id, parseInt(e.target.value) || 1)}
                              className="w-10 text-center border-x border-gray-300"
                            />
                            <button 
                              className="px-2 py-1 bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
                              onClick={() => updateQuantity(product.id, Math.min(product.stock, item.quantity + 1))}
                              disabled={item.quantity >= product.stock}
                            >
                              +
                            </button>
                          </div>
                          
                          <div className="flex flex-col items-end ml-4">
                            <span className="font-medium text-navy-800">
                              {formatPrice(price * item.quantity)}
                            </span>
                            {item.quantity > 1 && (
                              <span className="text-xs text-gray-500">
                                {formatPrice(price)} each
                              </span>
                            )}
                          </div>
                        </div>
                        
                        {/* Remove Button */}
                        <button 
                          onClick={() => removeItem(product.id)} 
                          className="sm:ml-4 mt-4 sm:mt-0 text-red-500 hover:text-red-600 transition-colors"
                          aria-label="Remove item"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </motion.li>
                    );
                  })}
                </ul>
                
                <div className="bg-gray-50 px-4 py-4 sm:px-6 border-t border-gray-200 flex justify-between items-center">
                  <button 
                    onClick={clearCart}
                    className="text-sm text-gray-600 hover:text-navy-700 font-medium"
                  >
                    Clear Cart
                  </button>
                  
                  <Link 
                    to="/products" 
                    className="text-sm text-amber-500 hover:text-amber-600 font-medium flex items-center"
                  >
                    <span>Continue Shopping</span>
                    <ArrowRight className="h-4 w-4 ml-1" />
                  </Link>
                </div>
              </motion.div>
            </div>
            
            {/* Order Summary */}
            <div>
              <motion.div 
                className="bg-white rounded-lg shadow-sm p-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.1 }}
              >
                <h2 className="text-xl font-semibold text-navy-800 mb-4">Order Summary</h2>
                
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Subtotal</span>
                    <span className="font-medium">{formatPrice(cart.total)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Shipping</span>
                    <span>Calculated at checkout</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Tax</span>
                    <span>Calculated at checkout</span>
                  </div>
                  <div className="pt-3 border-t border-gray-200 flex justify-between items-center">
                    <span className="text-lg font-semibold text-navy-800">Total</span>
                    <span className="text-xl font-bold text-navy-800">{formatPrice(cart.total)}</span>
                  </div>
                </div>
                
                <Link 
                  to="/checkout"
                  className="btn btn-primary w-full py-3 text-md font-semibold"
                >
                  Proceed to Checkout
                </Link>
                
                <div className="mt-6 flex items-start space-x-2 text-xs text-gray-500">
                  <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <p>
                    Shipping and taxes will be calculated at checkout. Payment processed securely.
                  </p>
                </div>
              </motion.div>
              
              {/* Accepted Payment Methods */}
              <div className="mt-4 bg-white rounded-lg shadow-sm p-4">
                <p className="text-xs text-gray-500 text-center">
                  We accept various payment methods for your convenience.
                </p>
                <div className="flex justify-center space-x-2 mt-2">
                  {/* Credit/Debit Cards */}
                  <div className="flex items-center justify-center w-12 h-8 bg-blue-600 rounded text-white text-xs font-bold">
                    VISA
                  </div>
                  <div className="flex items-center justify-center w-12 h-8 bg-red-600 rounded text-white text-xs font-bold">
                    MC
                  </div>
                  <div className="flex items-center justify-center w-12 h-8 bg-blue-800 rounded text-white text-xs font-bold">
                    AMEX
                  </div>
                  {/* UPI */}
                  <div className="flex items-center justify-center w-12 h-8 bg-orange-500 rounded text-white text-xs font-bold">
                    UPI
                  </div>
                  {/* Net Banking */}
                  <div className="flex items-center justify-center w-12 h-8 bg-green-600 rounded text-white text-xs font-bold">
                    NB
                  </div>
                  {/* Wallets */}
                  <div className="flex items-center justify-center w-12 h-8 bg-purple-600 rounded text-white text-xs font-bold">
                    💳
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CartPage;